import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ColumnPinningState, ColumnSizingState, SortingState } from '@tanstack/react-table';
import axios from 'axios';
import { isEmpty } from 'radash';
import { useEffect, useRef, useState } from 'react';

import { useOrganization } from '@/hooks/useOrganization';
import { useUser } from '@/hooks/useUser';
import { logger } from '@/services/logger';

import { endpoints, preferenceKeys } from './GridPreferencesConfig';

const USER_PREFERENCES_QUERY_KEY = ['user', 'preferences'] as const;

const fetchUserPreferences = () =>
    axios.get<Record<string, unknown>>('/api/v1/user/preferences').then((res) => res.data);

const ITEM_EDITOR_GRID_STATE_KEY_PREFIX = 'gridState:item[';

const isItemEditorGridStateKey = (k: string) =>
    typeof k === 'string' && k.startsWith(ITEM_EDITOR_GRID_STATE_KEY_PREFIX);

export const fieldPathToGridStateKey = (fieldPath: string): string => {
    const segments = fieldPath.split('.');
    const parts = segments.map((seg) => (/^\d+$/.test(seg) ? `[${seg}]` : `['${seg}']`));
    return `gridState:item${parts.join('')}`;
};

const parseGridState = (raw: unknown): Record<string, unknown> => {
    if (typeof raw === 'string') {
        try {
            return JSON.parse(raw);
        } catch {
            return {};
        }
    }
    return (raw || {}) as Record<string, unknown>;
};

const isValidSortEntry = (sort: any): sort is { id: string; desc: boolean } =>
    !!sort && typeof sort === 'object' && typeof sort.id === 'string' && sort.id.length > 0;

const columnId = (col: any): string => String(col?.id || col?.accessorKey || col?.name || '');

// LAX-10282: persisted sort on a now-unsortable column (enableSorting: false)
// must not be hydrated into the grid or sent as `sort=` to the items API.
const filterSortablePersistedSorting = (sorting: SortingState, columns: any[]): SortingState => {
    const unsortableIds = new Set(
        (columns || [])
            .filter((col: any) => col.enableSorting === false)
            .map(columnId)
            .filter(Boolean),
    );
    return sorting.filter((entry) => isValidSortEntry(entry) && !unsortableIds.has(entry.id));
};

const SELECT_COLUMN_WIDTH_PX = 40;

const withFixedSelectWidth = (sizing: ColumnSizingState): ColumnSizingState => {
    if (!('select' in sizing)) {
        return sizing;
    }
    return { ...sizing, select: SELECT_COLUMN_WIDTH_PX };
};

export const useGridPreferences = (
    columns: any,
    storedState: any,
    setStoredState: any,
    key: any,
    subKey?: string,
    defaultSortingState: SortingState = [],
) => {
    const { organization } = useOrganization();
    const { user } = useUser();
    const queryClient = useQueryClient();

    const organizationKey = `${organization?.name}${organization?.id}`;
    const accessKey = preferenceKeys[key] ?? (isItemEditorGridStateKey(key) ? key : '');
    // LAX-10039: legacy `getGridStateKey()` scopes the browse grid state under
    // `<category>-state` (or `default-state` when no category is set). Mirror
    // that here so switching categories loads its own persisted columns from
    // the server-side user-preferences blob. Grids that pass no `subKey`
    // (catalog, editor history) keep the legacy `default-state-...` name so
    // their existing saved preferences continue to load unchanged.
    const effectiveSubKey = subKey || 'default-state';
    const nestedStateKey = `${effectiveSubKey}-${key}-${organizationKey}-${organization?.dataModelName}`;

    const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(
        withFixedSelectWidth(storedState.columnSizing || {}),
    );
    const [columnVisibility, setColumnVisibility] = useState<{ [key: string]: boolean }>(
        storedState.columnVisibility || columns.reduce((acc: any, column: any) => {
            const key = column.id || column.accessorKey;
            acc[key] = column?.meta?.visible !== false;
            return acc;
        }, {})
    );
    // Hydrate from the persisted grid state so a reload restores the sort UI
    // (including multi-column priority) instead of dropping back to unsorted.
    // `applyPreferencesFromData` still runs for fresh sessions (empty
    // localStorage or mismatched org/user/dataModel) and will overwrite this
    // seed with the server-side value.
    const sortingOrDefault = (saved: SortingState): SortingState =>
        saved.length > 0 ? saved : defaultSortingState;

    const initialSortingState: SortingState = Array.isArray(storedState.sorting)
        ? sortingOrDefault(filterSortablePersistedSorting(storedState.sorting as SortingState, columns))
        : defaultSortingState;
    const [sortingState, setSortingState] = useState<SortingState>(initialSortingState);
    const appliedDefaultSortRef = useRef(defaultSortingState.length > 0);

    useEffect(() => {
        if (appliedDefaultSortRef.current || defaultSortingState.length === 0) {
            return;
        }
        appliedDefaultSortRef.current = true;
        setSortingState((current) => (current.length > 0 ? current : defaultSortingState));
    }, [defaultSortingState]);

    const [columnOrder, setColumnOrder] = useState<string[]>(() => {
        const activeColumns = storedState?.activeColumns || columns;
        return activeColumns.map((col: any) => col.id || col.accessorKey || col.name);
    });

    // User-controlled column pinning (LAX-10037). Seeded from `storedState`
    // when available so a page reload restores the pins immediately, ahead
    // of the server round-trip in `applyPreferencesFromData`.
    const isValidPinningState = (v: any): v is ColumnPinningState =>
        !!v && typeof v === 'object'
        && (v.left === undefined || Array.isArray(v.left))
        && (v.right === undefined || Array.isArray(v.right));
    const [columnPinning, setColumnPinning] = useState<ColumnPinningState>(
        isValidPinningState(storedState.columnPinning) ? storedState.columnPinning : {}
    );

    const { data: preferencesData } = useQuery({
        queryKey: USER_PREFERENCES_QUERY_KEY,
        queryFn: fetchUserPreferences,
    });

    const itemEditorPrefsHydrationIdRef = useRef<string | null>(null);
    const sortingStateRef = useRef<SortingState>(sortingState);
    const columnPinningRef = useRef<ColumnPinningState>(columnPinning);

    useEffect(() => {
        sortingStateRef.current = sortingState;
    }, [sortingState]);

    useEffect(() => {
        columnPinningRef.current = columnPinning;
    }, [columnPinning]);

    useEffect(() => {
        applyPreferencesFromData(preferencesData);
        // Note: do not clear storedState on cleanup. Doing so wipes the localStorage
        // cache on every preferencesData refetch (refetchOnMount: 'always') and on
        // unmount, which makes column order / sorting appear to "disappear" between
        // sessions even though they are still on the server.
    }, [preferencesData]);

    const refactorAccessorKeys = (newColumnVisibility: any) => {
        const editedIds = { ...newColumnVisibility };
        columns.forEach((column: any) => {
            const { accessorKey, id } = column;
            // eslint-disable-next-line no-prototype-builtins
            if (accessorKey && id && editedIds.hasOwnProperty(accessorKey)) {
                editedIds[id] = editedIds[accessorKey];
                delete editedIds[accessorKey];
            }
        });
        return editedIds;
    };

    const checkColumnsVisibilityState = () => {
        if (storedState.dataModel !== organization?.dataModelName) {
            setStoredState({});
            return false;
        }
        return true;
    };

    const checkCredentials = () => storedState.userId === user?.id && storedState.org === organizationKey;

    const applyPreferencesFromData = (data: Record<string, unknown> | undefined) => {
        if (!data) {
            return;
        }
        const itemEditorHydrationId = isItemEditorGridStateKey(key)
            ? `${key}|${organizationKey}|${user?.id ?? ''}`
            : '';
        if (isItemEditorGridStateKey(key) && itemEditorPrefsHydrationIdRef.current === itemEditorHydrationId) {
            return;
        }
        if (!isEmpty(storedState) && checkColumnsVisibilityState() && checkCredentials()) {
            return;
        }
        if (!checkCredentials()) {
            setStoredState({});
        }
        if (isEmpty(accessKey)) {
            return;
        }

        try {
            const gridState = parseGridState(data[accessKey]);
            const apiColumns = isItemEditorGridStateKey(key)
                ? (gridState?.columns || [])
                : ((gridState[nestedStateKey] as Record<string, unknown>)?.columns || []);

            const newColumnSizing: ColumnSizingState = {};
            const newColumnVisibility: { [key: string]: boolean } = {};
            const newSortingState: SortingState = [];
            const newColumnPinning: ColumnPinningState = { left: [], right: [] };

            const columnsActive = isEmpty(apiColumns) ? columns : apiColumns;

            columnsActive.forEach((col: any) => {
                const name = isEmpty(apiColumns) ? col.accessorKey : col.name;
                // TODO: Verify why apiColumns sometimes receives incorrect sizes from the API.
                newColumnSizing[name] = name === 'select'
                    ? SELECT_COLUMN_WIDTH_PX
                    : name === 'actions' ? 250 : Math.max(50, col.width || 150);

                const explicitlyAllHidden = columnsActive.every((column: any) => column?.visible === false);
                if (explicitlyAllHidden) {
                    newColumnVisibility[name] = true;
                } else if (isEmpty(apiColumns)) {
                    newColumnVisibility[name] = col?.meta?.visible !== false;
                } else {
                    newColumnVisibility[name] = isItemEditorGridStateKey(key) ? col.visible !== false : col.visible;
                }

                if (isValidSortEntry(col.sort)) {
                    newSortingState.push(col.sort);
                }

                // Legacy `application-controller.js toGridState` serializes
                // pin state as `pinned: "left" | "right" | ""` per column
                // (LAX-10037). Hydrate the TanStack columnPinning state from
                // that same key so browse users see their pinned columns
                // sticky on every reload.
                if (col.pinned === 'left') {
                    newColumnPinning.left!.push(name);
                } else if (col.pinned === 'right') {
                    newColumnPinning.right!.push(name);
                }
            });

            const sortableSortingState = sortingOrDefault(
                filterSortablePersistedSorting(newSortingState, columns),
            );

            setColumnSizing(withFixedSelectWidth(refactorAccessorKeys(newColumnSizing)));
            setColumnVisibility(refactorAccessorKeys(newColumnVisibility));
            setSortingState(sortableSortingState);
            setColumnOrder(columnsActive.map((col: any) => col.id || col.accessorKey || col.name));
            setColumnPinning(newColumnPinning);

            setStoredState({
                columnSizing: withFixedSelectWidth(newColumnSizing),
                columnVisibility: newColumnVisibility,
                org: organizationKey,
                userId: user?.id,
                dataModel: organization?.dataModelName,
                activeColumns: apiColumns,
                sorting: sortableSortingState,
                columnPinning: newColumnPinning,
            });

            if (isItemEditorGridStateKey(key)) {
                itemEditorPrefsHydrationIdRef.current = itemEditorHydrationId;
            }
        } catch (error) {
            logger.error('Error applying grid preferences:', error);
        }
    };

    const updateGridPreferences = async (updatedColumns: any[], sorting?: SortingState) => {
        const effectiveSorting = sorting ?? sortingStateRef.current ?? [];
        const sortByColumnId = new Map<string, { id: string; desc: boolean }>();
        effectiveSorting.forEach((entry) => {
            if (isValidSortEntry(entry)) {
                sortByColumnId.set(entry.id, { id: entry.id, desc: !!entry.desc });
            }
        });

        // Look the effective pin up from the current TanStack state instead
        // of trusting the column def, so a user pin toggle in the burger menu
        // is round-tripped to the server (LAX-10037). Falls back to the
        // column def's own `pinned` value for callers that still ship
        // pre-computed columns (item editor grids).
        const effectivePinning = columnPinningRef.current ?? {};
        const leftPinned = new Set(effectivePinning.left ?? []);
        const rightPinned = new Set(effectivePinning.right ?? []);

        const columnsPayload = updatedColumns.map((col) => {
            const colId = col.id ?? col.accessorKey;
            const sortForColumn = sortByColumnId.get(colId)
                ?? (isValidSortEntry(col.sort) ? col.sort : undefined);
            const pinned = leftPinned.has(colId)
                ? 'left'
                : rightPinned.has(colId)
                    ? 'right'
                    : (col.pinned || '');
            return {
                name: colId,
                width: colId === 'select' ? SELECT_COLUMN_WIDTH_PX : Math.max(50, col.width ?? 100),
                sort: sortForColumn || {},
                filters: col.filters || [{}],
                pinned,
                visible: col.visible !== undefined ? col.visible : true,
            };
        });

        const endpoint = endpoints[key] ?? (isItemEditorGridStateKey(key) ? `/api/v1/user/preferences/${encodeURIComponent(key)}` : undefined);
        if (!endpoint) {
            return;
        }

        let payload: Record<string, unknown>;
        if (isItemEditorGridStateKey(key)) {
            payload = { columns: columnsPayload };
        } else {
            // Multiple grids share the same preferences key (e.g. browse and catalog
            // both use 'online-catalog-grid-states'). The PUT endpoint replaces the
            // whole value, so we must merge into the existing blob to avoid
            // clobbering sibling grids' saved state (LAX-9727).
            const existingPrefs = queryClient.getQueryData<Record<string, unknown>>(USER_PREFERENCES_QUERY_KEY)
                ?? preferencesData
                ?? {};
            const existingForKey = parseGridState(existingPrefs[accessKey]);
            payload = {
                ...existingForKey,
                [nestedStateKey]: {
                    columns: columnsPayload,
                    org: organizationKey,
                    user: user?.id,
                    dataModel: organization?.dataModelName,
                    activeColumns: updatedColumns,
                    sorting: effectiveSorting,
                    'scrollFocus': {},
                    'grouping': {},
                    'treeView': {},
                    'pagination': {},
                },
            };
        }

        try {
            await axios.put(endpoint, payload);
            // Keep the React Query cache in sync so that the next call to
            // updateGridPreferences merges against the latest server state.
            queryClient.setQueryData<Record<string, unknown>>(USER_PREFERENCES_QUERY_KEY, (prev) => ({
                ...(prev ?? {}),
                [accessKey]: payload,
            }));
        } catch (error) {
            logger.error('Error updating grid preferences', error);
        }
    };

    // LAX-10039: reset the persisted grid state for the current key/sub-key,
    // mirroring the legacy `resetGridState` in `application-controller.js`
    // (removes the local blob and PUTs the parent preference minus this
    // sub-key back to the server). Item editor grids delete their whole
    // preference entry since they don't share the parent blob with siblings.
    const deleteGridPreferences = async () => {
        const endpoint = endpoints[key]
            ?? (isItemEditorGridStateKey(key) ? `/api/v1/user/preferences/${encodeURIComponent(key)}` : undefined);
        if (!endpoint) {
            return;
        }

        try {
            if (isItemEditorGridStateKey(key)) {
                // Item editor grids own their entire preference entry, so
                // clearing the sub-key means DELETEing the whole thing.
                await axios.delete(endpoint);
                queryClient.setQueryData<Record<string, unknown>>(USER_PREFERENCES_QUERY_KEY, (prev) => {
                    if (!prev) return prev;
                    const next = { ...prev };
                    delete next[accessKey];
                    return next;
                });
                return;
            }

            const existingPrefs = queryClient.getQueryData<Record<string, unknown>>(USER_PREFERENCES_QUERY_KEY)
                ?? preferencesData
                ?? {};
            const existingForKey = { ...parseGridState(existingPrefs[accessKey]) };
            delete existingForKey[nestedStateKey];

            await axios.put(endpoint, existingForKey);
            queryClient.setQueryData<Record<string, unknown>>(USER_PREFERENCES_QUERY_KEY, (prev) => ({
                ...(prev ?? {}),
                [accessKey]: existingForKey,
            }));
        } catch (error) {
            logger.error('Error deleting grid preferences', error);
        }
    };

    return {
        columnSizing,
        setColumnSizing,
        columnVisibility,
        setColumnVisibility,
        sortingState,
        setSortingState,
        updateGridPreferences,
        deleteGridPreferences,
        setColumnOrder,
        columnOrder,
        columnPinning,
        setColumnPinning,
    };
};
